// imports
import React, { useState, useEffect, useMemo, } from 'react';
import { BsInfoSquareFill } from "react-icons/bs";
import { Colors, Spacing } from "_styles";

function assetTrackingLocations(props) {

    //#region redux
    const beaconList = props.beaconList;
    const devices = props.devices;
    //#endregion

    //#region local states
    const [searchTerm, setSearchTerm] = useState('');
    const [debounceSearch, setDebounceSearch] = useState('');
    let debounceTimeout = null;
    //#endregion

    //#region useEffect
    useEffect(() => {

        const id = setTimeout(() => {
            setDebounceSearch(searchTerm.trim().toLowerCase());
        }, 200);

        return () => clearTimeout(id);

    }, [searchTerm]);
    //#endregion

    //#region memos
    const locationsByTitle = useMemo(() => {

        if (!devices?.length || !beaconList?.length) return {};

        const locs = {};
       
        for (const device of devices) {

            // skip devices that are not in location_fixed or location_failure state
            if (!['location_fixed', 'location_failure', 'heartbeat'].includes(device?.latest?.payloadType)) {
                continue;
            }

            let bleItems = [];

            // skip devices with gps fixed location and process ble items only for bluetooth fixed location or location failure
            if (device?.latest?.payloadType === 'location_fixed') {
                
                if (device?.latest?.positioningType === 'gps') continue;
                
                bleItems = device.latest?.bleItems || [];
            }
            else if (device?.latest?.payloadType === 'location_failure' || device?.latest?.payloadType === 'heartbeat') {
                
                let lastValidPosition = device?.lastValidPosition;

                if (!lastValidPosition || lastValidPosition?.positioningType === 'gps') continue;

                bleItems = lastValidPosition?.bleItems || [];
            }


            if (!bleItems || !bleItems.length) continue;

            const nearestBleItem = bleItems.reduce(
                (prev, curr) => (curr.rssi > prev.rssi ? curr : prev),
                { rssi: -1000 }
            );

            if (!nearestBleItem?.mac) continue;
            
            const beacon = beaconList.find(b => {
                const macs = [
                    b["macsBleMac"],
                    b["macsEddystoneMac"],
                    b["macsIbeaconMac"],
                    b["macsMainMac"],
                ]
                    .filter(Boolean)
                    .map(m => m.toLowerCase());

                return macs.includes(nearestBleItem.mac);
            });

            if (!beacon) continue;

            const title = beacon["currentLocationTitle"];

            if (!title) continue;

            if (!locs[title]) locs[title] = [];

            locs[title].push(device);
        }

        return locs;
        
    }, [devices, beaconList]);

    const filteredLocations = useMemo(() => {
        if (!debounceSearch) return locationsByTitle;

        return Object.fromEntries(
            Object.entries(locationsByTitle).filter(([title]) =>
                title.toLowerCase().includes(debounceSearch)
            )
        );
    }, [locationsByTitle, debounceSearch]);
    //#endregion

    return (
        <div className='border rounded flex-column border-end bg-white'>
            <div className='p-3'>
                <div className='bg-primary bg-opacity-10 p-2 mb-3 rounded d-flex align-items-center' style={{ gap: Spacing.spacing2 }}>
                    <strong><BsInfoSquareFill size={Spacing.SCALE_22} /> Assets near to an indoor location will be visible here</strong>
                </div>
                <div className='rounded mt-2 bg-light p-2'>
                    <input type="text"
                        className="form-control"
                        placeholder="Search Locations"
                        style={{ width: '50%' }}
                        onChange={(e) => { setSearchTerm(e.target.value) }}
                    />
                </div>
            </div>

            <div className='p-3' style={{ height: '60vh', overflowY: 'auto', marginTop: Spacing.spacing2 }}>
                {Object.entries(filteredLocations).map(([locTitle, devices], index) => (
                    <div key={locTitle} className='border rounded p-2 my-2 bg-dark bg-opacity-8'>
                        <div className='d-flex justify-content-between'>
                            <div>
                                <strong className='text-light'>{locTitle}</strong>
                            </div>
                            <div>
                                <span className='badge bg-primary'>{devices.length + " " + (devices.length > 1 ? ' Assets' : ' Asset')}</span>
                            </div>
                        </div>
                        <div className='mt-2 bg-white p-2' style={{ maxHeight: '200px', overflowY: 'auto' }}>
                            {devices.map((device, idx) => (
                                <div key={idx} className='border rounded p-1 my-1 d-flex justify-content-between' style={{ background: device?.latest?.payloadType == "location_failure" ? "#ff4d4d" : 'white' }}>
                                    {device?.meta?.deviceName}
                                    {
                                        device?.latest?.payloadType === "location_failure" &&
                                        <span className="showValidPos badge bg-primary mx-2"> Showing previous position </span>
                                    }
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

export default assetTrackingLocations;